import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../components/Card";
import Button from "../components/Button";
import SubirFactura from "../components/SubirFactura";
import API from "../api/api";

export default function DashboardCliente() {
  const nav = useNavigate();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ofertasAceptadas, setOfertasAceptadas] = useState([]);

  const [showFacturaForm, setShowFacturaForm] = useState(false);

  const getOperacionIdFromStorage = (ofertaId) => {
    try {
      const raw = localStorage.getItem("ofertaOperacionMap");
      if (!raw) return null;
      const map = JSON.parse(raw);
      return map?.[String(ofertaId)] || null;
    } catch (err) {
      console.error("Error leyendo operacionId desde storage:", err);
      return null;
    }
  };

  const fetchOfertasAceptadas = async () => {
    try {
      const usuarioActual = JSON.parse(localStorage.getItem("usuario") || "null");

      if (!usuarioActual || !usuarioActual.id) {
        console.error("No hay usuario logueado para cargar ofertas");
        return;
      }

      const resOps = await API.get("/operaciones/mis-movimientos/");
      const opsMap = {};
      (resOps.data || []).forEach((op) => {
        if (op.publicacion?.id) {
          opsMap[String(op.publicacion.id)] = op.id;
        }
      });

      const resPubs = await API.get("/publicaciones/mis/");
      const pubs = resPubs.data || [];

      const ofertasPorPub = await Promise.all(
        pubs.map(async (pub) => {
          try {
            const resOfertas = await API.get(`/ofertas/publicacion/${pub.id}/`);
            return { pub, ofertas: resOfertas.data || [] };
          } catch (err) {
            console.error(`Error cargando ofertas de publicación ${pub.id}:`, err);
            return { pub, ofertas: [] };
          }
        })
      );

      const todasOfertas = [];

      for (const { pub, ofertas } of ofertasPorPub) {
        const ofertasAceptadas = ofertas.filter((o) => o.estado === "aceptada");
        for (const oferta of ofertasAceptadas) {
          const operacionId =
            oferta.operacion_id ||
            getOperacionIdFromStorage(oferta.id) ||
            opsMap[String(pub.id)];
          todasOfertas.push({
            ...oferta,
            publicacion: pub,
            operacion_id: operacionId,
          });
        }
      }

      setOfertasAceptadas(todasOfertas);
    } catch (err) {
      console.error("Error cargando ofertas aceptadas:", err);
    }
  };

  const fetchPublicaciones = async () => {
    try {
      const res = await API.get("/publicaciones/mis/");
  
      console.log("Publicaciones del cliente:", res.data);
  
      setPosts(res.data || []);
    } catch (err) {
      console.error("Error al obtener publicaciones:", err);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPublicaciones();
    fetchOfertasAceptadas();
  }, []);

  if (loading)
    return (
      <p className="text-center mt-10 text-primary text-lg">
        Cargando publicaciones...
      </p>
    );

  return (
    <div className="space-y-6">

      <h1 className="text-2xl font-bold text-primary">Panel del Cliente</h1>
      <p className="text-slate-600">
        Desde aquí podés cargar facturas electrónicas para revisión y posterior publicación automática.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-cream to-sand/60 border-white/50">
          <div className="text-center">
            <p className="text-sm text-slate-600">Tus Publicaciones</p>
            <p className="text-3xl font-bold text-primary">{posts.length}</p>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-moss/15 to-cream border-moss/20">
          <div className="text-center">
            <p className="text-sm text-slate-600">Publicaciones Activas</p>
            <p className="text-3xl font-bold text-emerald-600">
              {posts.filter(p => p.estado === "activo").length}
            </p>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-gold/20 to-cream border-gold/30">
          <div className="text-center">
            <p className="text-sm text-slate-600">Operaciones Cerradas</p>
            <p className="text-3xl font-bold text-gold">
              {posts.filter(p => p.estado === "cerrado").length}
            </p>
          </div>
        </Card>
      </div>

      <Card className="bg-gradient-to-r from-ink to-stone-800 text-cream p-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold">¿Necesitás liquidez inmediata?</h2>
            <p className="text-cream/70 mt-1">
              Cargá tu factura electrónica y el backoffice la validará antes de publicarla.
            </p>
          </div>
          <Button
            onClick={() => setShowFacturaForm(!showFacturaForm)}
            className="bg-black text-primary hover:bg-gray-100 font-bold px-8 py-3 text-lg whitespace-nowrap"
          >
            {showFacturaForm ? "✕ Cerrar" : "➕ Nueva Publicación"}
          </Button>
        </div>
      </Card>

      {showFacturaForm && <SubirFactura />}

      <h2 className="text-xl font-semibold mt-6">Mis publicaciones</h2>

      {posts.length === 0 ? (
        <Card className="p-4 mt-2 text-center">
          <p className="text-slate-500">Aún no creaste ninguna publicación.</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {posts.map((post) => (
            <Card key={post.id}>
              <div className="flex items-center justify-between">

                <div>
                  <p className="uppercase text-xs text-slate-500">
                    {post.tipo}
                  </p>

                  <h3 className="font-semibold text-lg">{post.titulo}</h3>

                  <p className="text-sm text-slate-600">
                    Monto:{" "}
                    <b className="text-primary">
                      Gs {Number(post.monto).toLocaleString("es-PY")}
                    </b>
                  </p>

                  <p className="text-sm text-slate-600">
                    Vence: {post.vencimiento}
                  </p>

                  {post.descripcion && (
                    <p className="text-sm mt-1">{post.descripcion}</p>
                  )}

                  {post.factura_data && (
                    <div className="mt-2 text-xs text-slate-600 space-y-1">
                      <p>
                        Factura asociada: <b>{post.factura_data.cdc}</b>
                      </p>
                      <p>
                        RUC emisor: {post.factura_data.ruc_emisor || "N/D"} | Estado: {post.factura_data.estado}
                      </p>
                    </div>
                  )}
                </div>

                <span
                  className={`px-3 py-1 rounded text-white text-sm
                    ${post.estado === "activo"
                      ? "bg-emerald-600"
                      : post.estado === "pendiente"
                        ? "bg-yellow-600"
                        : "bg-slate-700"
                    }
                  `}
                >
                  {post.estado?.toUpperCase()}
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}

      <h2 className="text-xl font-semibold mt-6">Ofertas Aceptadas</h2>

      {ofertasAceptadas.length === 0 ? (
        <Card className="p-4 text-center">
          <p className="text-slate-500">No tienes ofertas aceptadas aún.</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {ofertasAceptadas.map((oferta) => {
            const operacionId = oferta.operacion_id;

            return (
            <Card key={oferta.id}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-primary">
                    {oferta.publicacion?.titulo}
                  </p>

                  <p className="text-sm text-slate-600">
                    Monto ofertado:{" "}
                    <b>Gs {Number(oferta.monto).toLocaleString("es-PY")}</b>
                  </p>

                  <p className="text-sm text-slate-600">
                    Comprador: {oferta.comprador_nombre || "N/A"}
                  </p>

                  <p className="text-sm text-slate-600">
                    Estado:{" "}
                    <span className="font-semibold text-emerald-600">
                      {oferta.estado?.toUpperCase()}
                    </span>
                  </p>
                </div>

                <Button
                  className="!bg-accent !text-black"
                  disabled={!operacionId}
                  onClick={() => {
                    if (!operacionId) return;
                    nav("/chat-operacion", {
                      state: { operacionId }
                    });
                  }}
                >
                  💬 Ir al chat
                </Button>
              </div>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
